// Workbook model for v2 projects (customer spec R00 §7–9), pure: no Office.js. Each builder
// returns { values, styles, merges, keyCol, ... } that excel/v2/workbookSync.js writes as is,
// and the parser reads a written sheet back into rows. A hidden KEY column tells every row
// what it is, so reading back does not depend on titles or numbers:
//   row 1: "costy|v2|<objectId>|<nodeId>|works|simple|total|plot"
//   blk|<nodeId>  loc|<location json>  work|<location json>  res|<MATERIAL|LABOR|EQUIPMENT>
//   note  hint  ltot  btot  sum  mk  end
//
// Row (server shape): { kind: "work"|"res"|"note", block_id, number, code, name, name_en, unit,
//   norm, qty, resource_type, unit_rate, note, location: { zone, floor, floor_to, mult, apt } }

import { markupChain } from "../../structure/markups.js";

export const SHEET_KEY_PREFIX = "costy|v2|";
const HEADER_ROWS = 7; // band, title, 4 header rows, 1 thin spacer
const FIRST_DATA_ROW = HEADER_ROWS + 1;
const NO_PRICE = "ფასი არ არის";

export const colLetter = (index) => {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

export const quoteSheet = (tab) => `'${String(tab).replace(/'/g, "''")}'`;

const RES_COLUMN = { MATERIAL: "matP", LABOR: "labP", EQUIPMENT: "eqP" };
const RES_TOTAL = { MATERIAL: "matT", LABOR: "labT", EQUIPMENT: "eqT" };

/** Column layout of a works sheet (Hard Cost) or a simple one (Soft Cost / OPEX). */
export function columnsFor(kind, { english = false, locationColumn = false } = {}) {
  const keys =
    kind === "simple"
      ? ["n", "name", ...(english ? ["en"] : []), "unit", "qty", "price", "total", "note"]
      : ["n", "code", "name", ...(english ? ["en"] : []), "unit", "norm", "qty", "matP", "matT", "labP", "labT", "eqP", "eqT", "total", "note"];
  if (locationColumn) keys.push("loc");
  keys.push("key");
  const col = Object.fromEntries(keys.map((k, i) => [k, colLetter(i)]));
  return { keys, col, width: keys.length };
}

export function locationLabel(loc) {
  if (!loc) return "";
  const parts = [];
  if (loc.zone) parts.push(`ზონა ${loc.zone}`);
  if (loc.floor !== undefined && loc.floor !== null && loc.floor !== "") {
    parts.push(loc.floor_to ? `სართ. ${loc.floor}–${loc.floor_to}${loc.mult > 1 ? ` ×${loc.mult}` : ""}` : `სართ. ${loc.floor}`);
  }
  if (loc.apt) parts.push(loc.apt);
  return parts.join(" · ");
}

const locKey = (loc) => (loc && Object.keys(loc).length ? JSON.stringify(loc) : "");
const parseLoc = (text) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
};

const emptyRow = (width) => Array(width).fill("");
const sumRange = (c, from, to) => (to >= from ? `=SUBTOTAL(9,${c}${from}:${c}${to})` : "=0");

/**
 * A discipline sheet.
 * spec: { objectId, nodeId, tab, kind: "works"|"simple", band, code, title, titleEn, english,
 *         blocks: [{ id, code, name, location: "none"|"vol"|..., locShow: "head"|"col" }],
 *         rows, markups (all; only at === "sheet" are used), detail: "detailed"|"tender", priceFactor }
 * Works without a block (a discipline without systems) sit in one implicit block (id null).
 * Returns { values, styles: [{ row, style }], merges, groups: [[from, to]], sumRow, finalRow, col, keyCol }.
 */
export function buildDisciplineSheet(spec) {
  const simple = spec.kind === "simple";
  const anyColumnLoc = spec.blocks.some((b) => b.locShow === "col" && b.location !== "none" && b.location !== "system");
  const { keys, col, width } = columnsFor(spec.kind, { english: spec.english, locationColumn: anyColumnLoc });
  const at = (k) => keys.indexOf(k);
  const factor = spec.priceFactor || 1;
  const values = [];
  const styles = [];
  const merges = [];
  const groups = [];
  const push = (row, style) => {
    values.push(row);
    if (style) styles.push({ row: values.length, style });
    return values.length;
  };
  const line = (cells, key, style) => {
    const row = emptyRow(width);
    Object.entries(cells).forEach(([k, v]) => {
      if (at(k) >= 0) row[at(k)] = v;
    });
    row[at("key")] = key;
    return push(row, style);
  };

  // header
  const r1 = emptyRow(width);
  r1[0] = spec.band || "";
  r1[at("key")] = `${SHEET_KEY_PREFIX}${spec.objectId}|${spec.nodeId}|${spec.kind}`;
  push(r1, "band");
  merges.push(`A1:${colLetter(width - 2)}1`);
  const r2 = emptyRow(width);
  r2[0] = spec.code ? `${spec.code} თავი` : "";
  r2[1] = spec.english && spec.titleEn ? `${spec.title} | ${spec.titleEn}` : spec.title;
  r2[at("key")] = `layout|${factor}|${spec.english ? 1 : 0}|${anyColumnLoc ? 1 : 0}`; // how to read this sheet back
  push(r2, "title");
  merges.push(`B2:${colLetter(width - 2)}2`);
  const head = (labels) => {
    const row = emptyRow(width);
    Object.entries(labels).forEach(([k, v]) => at(k) >= 0 && (row[at(k)] = v));
    return push(row, "head");
  };
  if (simple) {
    head({ n: "N", name: "დასახელება", en: "Description", unit: "განზ. ერთ.", qty: "რაოდ.", price: "ერთ. ფასი", total: "ჯამი", note: "შენიშვნა", loc: "ლოკაცია" });
    head({});
    head({});
  } else {
    head({ n: "N", code: "ნახაზის კოდი", name: "სამუშაოების დასახელება", en: "Work Description", unit: "განზ. ერთ.", norm: "ნორმატიული", matP: "მასალა", labP: "ხელფასი", eqP: "მექანიზმი/ტრანსპორტი", total: "სულ", note: "შენიშვნა", loc: "ლოკაცია" });
    head({ norm: "ერთ.", qty: "რაოდ.", matP: "ერთ. ფასი", matT: "ჯამი", labP: "ერთ. ფასი", labT: "ჯამი", eqP: "ერთ. ფასი", eqT: "ჯამი" });
    head({});
    ["matP", "labP", "eqP"].forEach((k) => merges.push(`${col[k]}3:${colLetter(at(k) + 1)}3`));
  }
  const r6 = emptyRow(width);
  keys.slice(0, -1).forEach((k, i) => (r6[i] = i + 1));
  push(r6, "colno");
  push(emptyRow(width), "thin");

  const totals = simple ? ["total"] : ["matT", "labT", "eqT", "total"];
  const subtotalCells = (from, to) => Object.fromEntries(totals.map((k) => [k, sumRange(col[k], from, to)]));
  const knownBlocks = new Set(spec.blocks.map((b) => b.id));
  const orphans = spec.rows.some((r) => !knownBlocks.has(r.block_id || null));
  const blockList = spec.blocks.length
    ? [...spec.blocks, ...(orphans ? [{ id: null, code: "", name: "სხვა სამუშაოები", location: "none", locShow: "col" }] : [])]
    : [{ id: null, code: "", name: spec.title, location: "none", locShow: "col" }];

  blockList.forEach((block, bi) => {
    const blockNo = bi + 1;
    const blockRows = block.id ? spec.rows.filter((r) => r.block_id === block.id) : spec.rows.filter((r) => !knownBlocks.has(r.block_id || null));
    if (block.id || spec.blocks.length) {
      const where = block.location && block.location !== "none" ? `   ·   ლოკაცია: ${block.locationLabel || block.location}` : "";
      line({ n: String(blockNo), [simple ? "name" : "code"]: `▣ ${block.code}  ${block.name}${where}` }, `blk|${block.id || ""}`, "block");
    }
    const first = values.length + 1;
    if (!blockRows.length) line({ name: "სამუშაოები ჯერ არ არის — მონიშნეთ ეს ბლოკი და „სამუშაოს დამატება“ აქ ჩასვამს" }, "hint", "hint");

    // works with their resources, grouped by location when shown as sub-headings
    const items = [];
    blockRows.forEach((r) => {
      if (r.kind === "res" && items.length && items[items.length - 1].work) items[items.length - 1].res.push(r);
      else items.push({ work: r.kind === "work" ? r : null, row: r, res: [] });
    });
    const byLoc = block.locShow === "head" && block.location !== "none" && block.location !== "system";
    const groupsOfItems = [];
    items.forEach((it) => {
      const k = byLoc && it.work ? locKey(it.work.location) : "";
      let g = groupsOfItems.find((x) => x.k === k);
      if (!g) groupsOfItems.push((g = { k, loc: it.work?.location || null, items: [] }));
      g.items.push(it);
    });

    let workNo = 0;
    groupsOfItems.forEach((g) => {
      const gFirst = values.length + 2;
      if (g.k) line({ name: locationLabel(g.loc) }, `loc|${g.k}`, "loc");
      g.items.forEach((it) => {
        if (!it.work) {
          const r = it.row;
          line({ name: r.name || "", note: r.note || "" }, "note", "note");
          return;
        }
        workNo += 1;
        const w = it.work;
        const number = `${blockNo}.${workNo}`;
        const name = w.name || "";
        if (simple) {
          const rowNo = values.length + 1;
          line(
            { n: number, name, en: w.name_en || "", unit: w.unit || "", qty: w.qty ?? "", price: w.unit_rate == null ? "" : w.unit_rate * factor, total: `=IF(${col.price}${rowNo}="",0,${col.qty}${rowNo}*${col.price}${rowNo})`, note: w.note || "", loc: locationLabel(w.location) },
            `work|${locKey(w.location)}`,
            "work"
          );
          return;
        }
        const wRow = values.length + 1;
        const rFrom = wRow + 1;
        const rTo = wRow + it.res.length;
        line(
          {
            n: number,
            code: w.code || "",
            name,
            en: w.name_en || "",
            unit: w.unit || "",
            qty: w.qty ?? "",
            ...(it.res.length ? subtotalCells(rFrom, rTo) : {}),
            note: w.note || "",
            loc: locationLabel(w.location),
          },
          `work|${locKey(w.location)}`,
          "work"
        );
        it.res.forEach((r, k) => {
          const row = values.length + 1;
          const type = RES_COLUMN[r.resource_type] ? r.resource_type : "MATERIAL";
          const priceCol = col[RES_COLUMN[type]];
          const cells = {
            n: `${number}.${k + 1}`,
            code: r.code || "",
            name: r.name || "",
            en: r.name_en || "",
            unit: r.unit || "",
            norm: r.norm ?? "",
            qty: `=IF(${col.norm}${row}="","",${col.norm}${row}*${col.qty}${wRow})`,
            [RES_COLUMN[type]]: r.unit_rate == null ? "" : r.unit_rate * factor,
            [RES_TOTAL[type]]: `=IF(${priceCol}${row}="",0,N(${col.qty}${row})*${priceCol}${row})`,
            total: `=SUM(${col.matT}${row},${col.labT}${row},${col.eqT}${row})`,
            note: r.unit_rate == null ? NO_PRICE : r.note || "",
          };
          line(cells, `res|${type}`, "res");
        });
        if (it.res.length && spec.detail === "tender") groups.push([rFrom, rTo]);
      });
      if (g.k) line({ name: `${locationLabel(g.loc)} — ჯამი`, ...subtotalCells(gFirst, values.length) }, "ltot", "ltot");
    });
    const last = values.length;
    if (block.id || spec.blocks.length) line({ name: `${block.name} — ჯამი`, ...subtotalCells(first, last) }, "btot", "btot");
  });

  // sheet end: ჯამი, then each sheet markup on the running total (Hard Cost only)
  const lastData = values.length;
  const sumRow = line({ name: "ჯამი", ...subtotalCells(FIRST_DATA_ROW, lastData) }, "sum", "sum");
  let finalRow = sumRow;
  if (!simple && !spec.inUnit) {
    (spec.markups || [])
      .filter((m) => m.on && m.at === "sheet")
      .forEach((m) => {
        const pct = Number(String(m.pct).replace(",", ".")) || 0;
        const mk = line({ name: `${m.name} ${pct}%`, total: `=${col.total}${finalRow}*${pct}/100` }, "mk", "mk");
        finalRow = line({ name: "ჯამი", total: `=${col.total}${finalRow}+${col.total}${mk}` }, "end", "sum");
      });
  }
  if (groups.length) values[0][at("key")] += "|t"; // grouped rows: ungroup before the next rewrite
  return { values, styles, merges, groups, sumRow, finalRow, col, keyCol: col.key, width, kind: spec.kind };
}

/**
 * Read a written discipline sheet back into rows. Rows the user typed (no key) inside a block
 * are works when they have a quantity and no norm, resources when they have a norm or a price.
 */
export function parseDisciplineSheet(values) {
  const header = values[0] || [];
  const keyIdx = header.findIndex((v) => String(v).startsWith(SHEET_KEY_PREFIX));
  if (keyIdx < 0) return null;
  const [, , objectId, nodeId, realKind] = String(header[keyIdx]).split("|"); // 6th part "t": grouped
  const [, factorText, englishFlag, locFlag] = String((values[1] || [])[keyIdx] || "layout|1|0|0").split("|");
  const priceFactor = Number(factorText) || 1;
  const { keys } = columnsFor(realKind, { english: englishFlag === "1", locationColumn: locFlag === "1" });
  const get = (row, k) => (keys.indexOf(k) >= 0 ? row[keys.indexOf(k)] : "");
  const text = (v) => (v === null || v === undefined ? "" : String(v).trim());
  const num = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };
  const price = (v) => (num(v) == null ? null : Math.round((num(v) / priceFactor) * 1e6) / 1e6);

  const rows = [];
  let block = null;
  let loc = null;
  for (let i = HEADER_ROWS; i < values.length; i++) {
    const row = values[i];
    const key = text(row[keyIdx]);
    const [tag, rest] = [key.split("|")[0], key.slice(key.indexOf("|") + 1)];
    if (tag === "sum" || tag === "end") break;
    if (tag === "blk") {
      block = rest || null;
      loc = null;
      continue;
    }
    if (tag === "loc") {
      loc = parseLoc(rest);
      continue;
    }
    if (["hint", "ltot", "btot", "mk"].includes(tag)) continue;
    const name = text(get(row, "name"));
    const base = {
      block_id: block,
      number: text(get(row, "n")) || null,
      code: text(get(row, "code")) || null,
      name: name || null,
      name_en: text(get(row, "en")) || null,
      unit: text(get(row, "unit")) || null,
    };
    if (tag === "note") {
      rows.push({ ...base, kind: "note", note: text(get(row, "note")) || null });
      continue;
    }
    if (realKind === "simple") {
      if (!name && num(get(row, "qty")) == null) continue;
      rows.push({ ...base, kind: "work", qty: num(get(row, "qty")), unit_rate: price(get(row, "price")), note: text(get(row, "note")) || null, location: tag === "work" ? parseLoc(rest) : loc });
      continue;
    }
    const resType = tag === "res" ? rest : ["MATERIAL", "LABOR", "EQUIPMENT"].find((t) => num(get(row, RES_COLUMN[t])) != null);
    const isRes = tag === "res" || (tag !== "work" && (num(get(row, "norm")) != null || !!resType));
    if (!name && !base.code && num(get(row, "qty")) == null && num(get(row, "norm")) == null) continue;
    if (isRes) {
      const t = resType || "MATERIAL";
      const note = text(get(row, "note"));
      rows.push({ ...base, kind: "res", norm: num(get(row, "norm")), resource_type: t, unit_rate: price(get(row, RES_COLUMN[t])), note: note && note !== NO_PRICE ? note : null });
    } else {
      rows.push({ ...base, kind: "work", qty: num(get(row, "qty")), note: text(get(row, "note")) || null, location: tag === "work" ? parseLoc(rest) : loc });
    }
  }
  return { objectId, nodeId, kind: realKind, rows };
}

/**
 * An object's TOTAL. lines: [{ id, depth, code, name, role: "sum"|"sheet", category, tab, sheetKind,
 * sumRow, finalRow, col, children: [ids] }] in tree order (roles from excelRoles; hidden/off left out).
 */
export function buildTotalSheet({ objectId, title, lines, markups, english }) {
  const width = 8; // A N · B name · C material · D labour · E machinery · F total · G % · H key
  const values = [];
  const styles = [];
  const merges = [];
  const push = (cells, key, style) => {
    const row = emptyRow(width);
    cells.forEach((v, i) => (row[i] = v ?? ""));
    row[7] = key;
    values.push(row);
    if (style) styles.push({ row: values.length, style });
    return values.length;
  };
  push([title.project ? `პროექტის დასახელება: ${title.project}` : ""], `${SHEET_KEY_PREFIX}${objectId}|TOTAL|total`, "band");
  merges.push("A1:G1");
  push(["", `თარიღი: ${title.date || ""}`, "", "", `დამკვეთი: ${title.client || ""}`], "", "info");
  push(["", `დოკ. ნომერი: ${title.docNo || ""}`, "", "", `მოამზადა: ${title.preparedBy || ""}`], "", "info");
  push(["", `დოკ. დასახელება: ${title.docTitle || ""}`, "", "", `მდებარეობა: ${title.location || ""}`], "", "info");
  push(["", title.objectName || ""], "", "title");
  push(["N", english ? "სამუშაოების დასახელება | Description" : "სამუშაოების დასახელება", "მასალა", "ხელფასი", "მექანიზმი", "ჯამური ღირებულება", "%"], "", "head");
  push([], "", "thin");

  const rowOf = {};
  const catRows = {};
  const CAT_TITLES = { hard: "პირდაპირი დანახარჯები (Hard Cost)", soft: "არაპირდაპირი დანახარჯები (Soft Cost)", opex: "საოპერაციო დანახარჯები (OPEX)" };
  const cats = ["hard", "soft", "opex"].filter((c) => lines.some((l) => l.category === c));
  const capexRow = cats.some((c) => c !== "opex") ? push(["", "კაპიტალური დანახარჯები (CAPEX)"], "capex", "cat") : null;
  cats.forEach((cat) => {
    catRows[cat] = push(["", CAT_TITLES[cat]], `cat|${cat}`, "cat");
    lines
      .filter((l) => l.category === cat)
      .forEach((l) => {
        rowOf[l.id] = push([l.code, l.name], `line|${l.id}`, l.depth === 1 ? "line1" : "line");
      });
  });
  const set = (row, i, v) => (values[row - 1][i] = v);
  const linesOf = (cat) => lines.filter((l) => l.category === cat);
  lines.forEach((l) => {
    const r = rowOf[l.id];
    if (l.role === "sum") {
      const kids = l.children.filter((id) => rowOf[id]).map((id) => rowOf[id]);
      [2, 3, 4, 5].forEach((i) => set(r, i, kids.length ? `=SUM(${kids.map((k) => `${colLetter(i)}${k}`).join(",")})` : "=0"));
    } else if (l.sheetKind === "simple") {
      set(r, 5, `=${quoteSheet(l.tab)}!${l.col.total}${l.sumRow}`);
    } else {
      set(r, 2, `=${quoteSheet(l.tab)}!${l.col.matT}${l.sumRow}`);
      set(r, 3, `=${quoteSheet(l.tab)}!${l.col.labT}${l.sumRow}`);
      set(r, 4, `=${quoteSheet(l.tab)}!${l.col.eqT}${l.sumRow}`);
      set(r, 5, `=${quoteSheet(l.tab)}!${l.col.total}${l.finalRow}`);
    }
  });
  cats.forEach((cat) => {
    const tops = linesOf(cat).filter((l) => l.depth === 1).map((l) => rowOf[l.id]);
    [2, 3, 4, 5].forEach((i) => set(catRows[cat], i, tops.length ? `=SUM(${tops.map((t) => `${colLetter(i)}${t}`).join(",")})` : "=0"));
  });
  if (capexRow) {
    const capexCats = cats.filter((c) => c !== "opex").map((c) => catRows[c]);
    [2, 3, 4, 5].forEach((i) => set(capexRow, i, `=SUM(${capexCats.map((r) => `${colLetter(i)}${r}`).join(",")})`));
  }
  push([], "", "thin");
  const catCells = (i) => cats.map((c) => `${colLetter(i)}${catRows[c]}`).join(",");
  const sumRow = push(["", "ჯამი", ...[2, 3, 4, 5].map((i) => (cats.length ? `=SUM(${catCells(i)})` : "=0"))], "sum", "sum");
  const finalRow = appendMarkups(push, markups, sumRow, "F");
  for (let r = FIRST_DATA_ROW; r < sumRow; r++) if (values[r - 1][7]) values[r - 1][6] = `=IF($F$${sumRow}=0,"",F${r}/$F$${sumRow})`;
  return { values, styles, merges, sumRow, finalRow, width, keyCol: "H" };
}

function appendMarkups(push, markups, fromRow, totalCol) {
  let finalRow = fromRow;
  (markups || [])
    .filter((m) => m.on && m.at === "total")
    .forEach((m) => {
      const pct = Number(String(m.pct).replace(",", ".")) || 0;
      const mk = push(["", `${m.name} ${pct}%`, "", "", "", `=${totalCol}${finalRow}*${pct}/100`], "mk", "mk");
      finalRow = push(["", "ჯამი", "", "", "", `=${totalCol}${finalRow}+${totalCol}${mk}`], "end", "sum");
    });
  return finalRow;
}

/** The plot sheet „ნაკვეთი“ (several objects): each object's ჯამი before TOTAL markups, then the markups once. */
export function buildPlotSheet({ title, objects, markups }) {
  const width = 8;
  const values = [];
  const styles = [];
  const push = (cells, key, style) => {
    const row = emptyRow(width);
    cells.forEach((v, i) => (row[i] = v ?? ""));
    row[7] = key;
    values.push(row);
    if (style) styles.push({ row: values.length, style });
    return values.length;
  };
  push([title.project ? `პროექტის დასახელება: ${title.project}` : ""], `${SHEET_KEY_PREFIX}plot|PLOT|plot`, "band");
  push(["", `თარიღი: ${title.date || ""}`, "", "", `დამკვეთი: ${title.client || ""}`], "", "info");
  push(["", `დოკ. დასახელება: ${title.docTitle || ""}`, "", "", `მდებარეობა: ${title.location || ""}`], "", "info");
  push(["", title.plotName || "ნაკვეთი"], "", "title");
  push([], "", "thin");
  push(["N", "ობიექტი", "მასალა", "ხელფასი", "მექანიზმი", "ჯამური ღირებულება", "%"], "", "head");
  push([], "", "thin");
  const first = values.length + 1;
  objects.forEach((o) => {
    const ref = (c) => `=${quoteSheet(o.total)}!${c}${o.sumRow}`;
    push([String(o.number), o.name, ref("C"), ref("D"), ref("E"), ref("F")], `obj|${o.objectId}`, "line1");
  });
  const last = values.length;
  const sumRow = push(["", "ჯამი", ...["C", "D", "E", "F"].map((c) => `=SUM(${c}${first}:${c}${last})`)], "sum", "sum");
  const finalRow = appendMarkups(push, markups, sumRow, "F");
  for (let r = first; r <= last; r++) values[r - 1][6] = `=IF($F$${sumRow}=0,"",F${r}/$F$${sumRow})`;
  return { values, styles, merges: ["A1:G1"], sumRow, finalRow, width, keyCol: "H" };
}

export { markupChain };
